import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
  afterRenderEffect,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { SbDateTimeMode } from './sb-date-time-format';
import {
  FieldState,
  SEGMENT_NAME,
  SbKeyResult,
  SbSegContext,
  blur,
  commit,
  displayText,
  emptyState,
  focusSegment,
  insert,
  key,
  sameEntry,
  segBounds,
  segValueText,
  segView,
  segmentKinds,
  segmentLiterals,
  visibleParts,
  waiting,
} from './sb-date-time-segments';
import { nowIn } from './sb-quick-picks';

/** What an edit in the field did. */
export interface SbFieldEdit {
  state: FieldState;
  /** The last segment in typing order was completed by typing. */
  filled: boolean;
  /** What the field shows changed (`sameEntry`); false for focus moving in, out or between segments only. */
  changed: boolean;
  /** Ctrl+Z or Ctrl+Y went back or forward over a step made together with another field's (`apply`). */
  crossed?: { step: object; direction: -1 | 1 };
}

/** The most whole states the field keeps for Ctrl+Z in one visit. */
const HISTORY = 50;

/** iOS VoiceOver cannot focus spinbuttons; React Aria uses textboxes there. */
const isIOS = (): boolean =>
  typeof navigator !== 'undefined' && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const coarsePrimary = (): boolean => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

const isUndo = (event: KeyboardEvent): boolean =>
  (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'z';

/**
 * Kept text on a phone: no Esc or Ctrl+Z brings it back, so Backspace and Delete leave it; typing,
 * a paste or a pick replaces it, and the clear button empties it.
 */
const keepsKeptText = (state: FieldState, keyName: string): boolean =>
  state.kept !== null && (keyName === 'Backspace' || keyName === 'Delete') && coarsePrimary();

const isRedo = (event: KeyboardEvent): boolean =>
  (event.ctrlKey || event.metaKey) && !event.altKey && (event.key.toLowerCase() === 'y' || (event.shiftKey && event.key.toLowerCase() === 'z'));

/**
 * The `beforeinput` deletions that are a Backspace or Delete (a phone keyboard sends no usable
 * keydown). Any other deletes nothing in the field: `deleteCompositionText`, which WebKit sends
 * before a composition's text goes in (the text then goes in once, at `compositionend`), a cut, a drag.
 */
const DELETIONS: Readonly<Partial<Record<string, 'Backspace' | 'Delete'>>> = {
  deleteContentBackward: 'Backspace',
  deleteWordBackward: 'Backspace',
  deleteSoftLineBackward: 'Backspace',
  deleteHardLineBackward: 'Backspace',
  deleteContentForward: 'Delete',
  deleteWordForward: 'Delete',
  deleteSoftLineForward: 'Delete',
  deleteHardLineForward: 'Delete',
};

/** Keys the model acts on, besides one printable character. */
const MODEL_KEYS = new Set(['Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'Tab']);

/**
 * The segmented field: one labelled group of month, day, year, hour, minute and AM/PM segments
 * (WAI-ARIA spinbuttons, as React Aria's DateField), or the kept text a speaker session saved.
 * It renders a `FieldState` and turns keys, `beforeinput`, compositions, pastes and clicks into
 * calls on `sb-date-time-segments.ts`. The browser never edits a segment: every `beforeinput` is
 * cancelled, and the text of every segment is written here from the state, never bound, so a
 * composition that replaced a text node cannot leave a stale display behind.
 *
 * Nothing in it is editable at rest: Chrome moves a press on blank page space to the nearest editable
 * element, whose segment the keys people scroll with would then change, and a phone raises its
 * keyboard for it. The whole field turns editable before focus lands in it (`setEditing`), and stays
 * so while focus is inside, so a phone's keyboard stays up as typing moves on between segments.
 *
 * Its own Ctrl+Z history holds whole states the field showed, from focus entering until the
 * field that hosts it ends the visit (`endVisit`, when focus leaves the field and its panel).
 */
@Component({
  selector: 'app-sb-date-time-field',
  templateUrl: './sb-date-time-field.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sb-dt__field',
    role: 'group',
    '[attr.id]': 'fieldId() || null',
    '[attr.aria-labelledby]': 'nameId() || null',
    '[attr.aria-label]': 'nameId() ? null : name() || null',
    '[attr.aria-describedby]': 'describedBy() || null',
    '[attr.aria-disabled]': 'disabled() ? "true" : null',
    '[attr.aria-readonly]': 'readonly() ? "true" : null',
    '[class.sb-dt__field--floated]': 'floated()',
    '[class.sb-dt__field--focused]': 'focused()',
    '[class.sb-dt__field--invalid]': 'invalid()',
    '(focusin)': 'onFocusIn($event)',
    '(focusout)': 'onFocusOut($event)',
    '(keydown)': 'onKeydown($event)',
    '(beforeinput)': 'onBeforeInput($event)',
    '(input)': 'onInput($event)',
    '(compositionstart)': 'onCompositionStart()',
    '(compositionend)': 'onCompositionEnd($event)',
    '(paste)': 'onPaste($event)',
    '(drop)': 'onDrop($event)',
    '(copy)': 'onCopy($event)',
    '(cut)': 'onCopy($event)',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointercancel)': 'onPointerCancel()',
    '(mousedown)': 'onMouseDown($event)',
    '(click)': 'onClick()',
  },
})
export class SbDateTimeFieldComponent implements OnDestroy {
  readonly mode = input.required<SbDateTimeMode>();
  readonly state = model<FieldState>(emptyState('date'));
  readonly fieldId = input('');
  /** The id of the element that names the group (the field's label); without one, `name` names it. */
  readonly nameId = input('');
  readonly name = input('');
  readonly describedBy = input('');
  /** The field shows an error: every segment is described by it and marked invalid. */
  readonly invalid = input(false);
  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly required = input(false);
  readonly zone = input<string | null>(null);
  /** The hidden "Saved as entered…" line that describes kept text. */
  readonly keptHintId = input('');
  /** The label stays up while something shows (a placeholder, a value, or focus). */
  readonly floatLabel = input(false);

  /** Keys, pastes, undo: the state changed and the host writes its value. */
  readonly edited = output<SbFieldEdit>();
  /** Alt+Down, or a click on the field's blank end. */
  readonly openRequested = output<'keyboard' | 'pointer'>();
  /** Focus left the group (for the panel, the buttons or the page). */
  readonly left = output<FocusEvent>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly segEls = viewChildren<ElementRef<HTMLElement>>('seg');
  private readonly litEls = viewChildren<ElementRef<HTMLElement>>('lit');
  private readonly keptEl = viewChild.required<ElementRef<HTMLElement>>('kept');

  readonly focused = signal(false);
  readonly kinds = computed(() => segmentKinds(this.mode()));
  readonly literals = computed(() => segmentLiterals(this.mode()));
  readonly editable = computed(() => !this.disabled() && !this.readonly());
  readonly ios = isIOS();
  readonly segIds = computed(() => this.kinds().map((kind) => `${this.fieldId() || 'sb-dt'}-${kind}`));
  readonly hasValue = computed(() => {
    const state = this.state();
    return state.kept !== null || state.segs.some((seg) => seg.value !== null || !!seg.typed || !!seg.refused);
  });
  readonly floated = computed(() => this.focused() || this.hasValue() || this.floatLabel());

  /** Whole states for Ctrl+Z, and where in them the field is. A state with a digit waiting is not one. */
  private history: FieldState[] = [];
  private at = -1;
  /** The kept text the field held when this visit began, while the first edit has not been undone. */
  private keptEntry: FieldState | null = null;
  /** History states made by one change across fields (a range's Clear or preset), by that change. */
  private readonly together = new WeakMap<FieldState, object>();
  /** The current history step was made by typing, and typing has gone on since: further keys join it. */
  private typingStep = false;
  private inVisit = false;
  /** A `compositionend` already passed its data in; the `input` after it must not again. */
  private composed = false;
  /** The AM/PM segment was already focused when the press that ends in this click began. */
  private pressedFocusedPeriod = false;
  /** Focus is inside the field, or a press on a segment or kept text began. */
  private editing = false;
  private observer: MutationObserver | null = null;
  private readonly onSelectionChange = () => this.collapseSelection();
  /**
   * A press anywhere but on a segment or kept text finds nothing editable, so the browser leaves focus
   * where the press says and puts no caret in the field. A press whose default was cancelled (the
   * panel's blank areas) keeps focus here, and the field editable.
   */
  private readonly onPagePress = (event: MouseEvent) => {
    if (this.indexOf(event.target) < 0 && event.target !== this.keptEl().nativeElement) {
      this.setEditing(false);
      setTimeout(() => this.setEditing(this.hasFocus()));
    }
  };

  constructor() {
    afterRenderEffect(() => {
      this.state();
      this.focused();
      this.mode();
      this.render();
    });
    if (typeof MutationObserver === 'function') {
      // An extension or the spellchecker that touches a segment's text is undone at once.
      this.observer = new MutationObserver(() => this.render());
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    document.removeEventListener('selectionchange', this.onSelectionChange);
    document.removeEventListener('mousedown', this.onPagePress, true);
  }

  // ─── Called by the host ────────────────────────────────────

  /**
   * A pick, a column choice, Clear: one history step. `step` marks a change made in other fields
   * too (both halves of a range): Ctrl+Z or Ctrl+Y over it in one of them takes the others with it.
   */
  apply(next: FieldState, step?: object): void {
    this.beginVisit();
    this.set({ ...next, focus: this.focused() ? Math.max(next.focus, this.state().focus, 0) : next.focus }, false, step);
  }

  /** Ctrl+Z or Ctrl+Y in another field went over `step`: this field goes over its own part of it, if that is where it stands. */
  follow(step: object, direction: -1 | 1): void {
    const current = this.history[this.at];
    const over = this.history[direction < 0 ? this.at : this.at + 1];
    if (over && this.together.get(over) === step && current && sameEntry(current, this.state())) {
      this.travel(direction, false);
    }
  }

  /** The page wrote a value: it replaces everything, history included. */
  reset(next: FieldState): void {
    const focus = this.focused() ? Math.max(this.state().focus, 0) : -1;
    this.state.set({ ...next, focus, run: focus <= 0 });
    this.history = this.inVisit ? [this.state()] : [];
    this.at = this.history.length - 1;
    this.typingStep = false;
    this.keptEntry = this.inVisit && next.kept !== null ? this.state() : null;
    this.render();
  }

  /** Enter, Done, a pick, or focus leaving the field and its panel: the AM/PM rule. */
  commit(): boolean {
    const result = commit(this.state());
    if (result.state === this.state()) {
      return false;
    }
    this.set(result.state);
    if (result.announce) {
      this.announcer.announce(result.announce, 'polite');
    }
    return true;
  }

  /** Focus left the field and its panel: the edit stands, kept text it replaced stays replaced. */
  endVisit(): void {
    this.inVisit = false;
    this.history = [];
    this.at = -1;
    this.typingStep = false;
    this.keptEntry = null;
    if (!this.hasFocus() && this.state().focus >= 0) {
      this.state.set(blur(this.state()));
    }
  }

  /** Esc, or Ctrl+Z as the first undo, after kept text gave way: it is back, as it was. */
  restoreKept(): boolean {
    const kept = this.keptEntry;
    if (!kept || this.state().kept !== null) {
      return false;
    }
    this.state.set({ ...kept, focus: this.focused() ? 0 : -1 });
    this.history = [this.state()];
    this.at = 0;
    this.typingStep = false;
    this.render();
    this.emit(false);
    return true;
  }

  /** Whether kept text gave way in this visit. */
  keptGaveWay(): boolean {
    return !!this.keptEntry && this.state().kept === null;
  }

  hasFocus(): boolean {
    return this.host.nativeElement.contains(document.activeElement);
  }

  /** Focuses a segment: the first empty one (a click on the label), the first, or the last. */
  focus(which: 'first' | 'last' | 'firstEmpty' = 'first', preventScroll = false): void {
    const state = this.state();
    if (state.kept !== null) {
      this.focusElement(this.keptEl().nativeElement, preventScroll);
      return;
    }
    const empty = state.segs.findIndex((seg) => seg.value === null);
    const index = which === 'last' ? state.segs.length - 1 : which === 'firstEmpty' && empty >= 0 ? empty : 0;
    this.focusElement(this.segEls()[index]?.nativeElement, preventScroll);
  }

  /** Focus arrives from the half before, typed whole: separators typed next belong to it. */
  enterByTyping(): void {
    this.state.set({ ...this.state(), focus: 0, enteredBy: 'advance', run: true, fresh: true });
    this.focusElement(this.segEls()[0]?.nativeElement, true);
  }

  focusAt(index: number, preventScroll = false): void {
    if (this.state().kept !== null) {
      this.focus('first', preventScroll);
      return;
    }
    this.focusElement(this.segEls()[Math.min(index, this.segEls().length - 1)]?.nativeElement, preventScroll);
  }

  /** The text the field shows, for copy and for messages. */
  shownText(): string {
    return displayText(this.state(), this.focused());
  }

  // ─── Rendering ─────────────────────────────────────────────

  segName(index: number): string {
    return SEGMENT_NAME[this.kinds()[index]];
  }

  segLabelledBy(index: number): string {
    return [this.segIds()[index], this.nameId()].filter(Boolean).join(' ');
  }

  segRole(): string {
    return this.ios ? 'textbox' : 'spinbutton';
  }

  segInputMode(index: number): string {
    if (this.kinds()[index] !== 'period') {
      return 'numeric';
    }
    return coarsePrimary() ? 'none' : 'text';
  }

  segDescribedBy(index: number): string | null {
    return index === 0 || this.invalid() ? this.describedBy() || null : null;
  }

  ariaValueNow(index: number): number | null {
    return this.ios ? null : this.state().segs[index]?.value ?? null;
  }

  ariaValueText(index: number): string | null {
    return this.ios ? null : segValueText(this.state(), index);
  }

  ariaBounds(index: number): [number, number] | null {
    return this.ios ? null : segBounds(this.state(), index);
  }

  keptDescribedBy(): string | null {
    return [this.keptHintId(), this.describedBy()].filter(Boolean).join(' ') || null;
  }

  segInvalid(index: number): boolean {
    return !!this.state().segs[index]?.refused || this.invalid();
  }

  /**
   * Writes every segment's digits, letters and visibility, the literals and the kept text from the
   * state. The one place that writes the group's text; a template binding inside a contenteditable
   * would update a text node a composition had replaced.
   */
  private render(): void {
    const segs = this.segEls();
    const state = this.state();
    if (segs.length !== state.segs.length) {
      return;
    }
    this.observer?.disconnect();
    this.writeEditable();
    const focused = this.focused();
    const shown = visibleParts(state, focused);
    const kept = state.kept !== null;
    const keptEl = this.keptEl().nativeElement;
    state.segs.forEach((seg, i) => {
      const el = segs[i].nativeElement;
      const view = segView(seg, focused);
      setText(el, kept || !shown.segs[i] ? '' : view.text);
      el.dataset['rest'] = kept || !shown.segs[i] ? '' : view.rest;
      el.classList.toggle('sb-dt__seg--refused', !!seg.refused);
      el.classList.toggle('sb-dt__seg--active', focused && state.focus === i);
    });
    this.litEls().forEach((lit, i) => setText(lit.nativeElement, !kept && shown.literals[i] ? this.literals()[i] : ''));
    setText(keptEl, kept ? (state.kept as string) : '');
    // What shows comes first and takes focus before what goes is hidden, so focus never drops to nothing.
    const showing: HTMLElement[] = kept ? [keptEl] : [...segs.map((el) => el.nativeElement), ...this.litEls().map((el) => el.nativeElement)];
    const hiding: HTMLElement[] = kept ? [...segs.map((el) => el.nativeElement), ...this.litEls().map((el) => el.nativeElement)] : [keptEl];
    showing.forEach((el) => (el.hidden = false));
    this.syncFocus();
    hiding.forEach((el) => (el.hidden = true));
    this.observer?.observe(this.host.nativeElement, { childList: true, characterData: true, subtree: true });
  }

  /** While the field has focus, the DOM focus follows the state's focused segment. */
  private syncFocus(): void {
    const state = this.state();
    if (!this.focused() || state.focus < 0) {
      return;
    }
    const target = state.kept !== null ? this.keptEl().nativeElement : this.segEls()[state.focus]?.nativeElement;
    if (target && document.activeElement !== target) {
      target.focus({ preventScroll: true });
    }
  }

  // ─── Events ────────────────────────────────────────────────

  onFocusIn(event: FocusEvent): void {
    const index = this.indexOf(event.target);
    const kept = event.target === this.keptEl().nativeElement;
    if (index < 0 && !kept) {
      return;
    }
    if (!this.focused()) {
      document.addEventListener('selectionchange', this.onSelectionChange);
    }
    // Focus that came by Tab or back with the window found nothing editable, so it has no caret yet,
    // which an input method and a paste need.
    if (!this.editing && this.editable()) {
      this.setEditing(true);
      document.getSelection()?.collapse(event.target as Node, 0);
    }
    this.focused.set(true);
    this.beginVisit();
    const state = this.state();
    if (kept || index === state.focus) {
      if (state.focus < 0) {
        this.state.set({ ...state, focus: 0 });
      }
      return;
    }
    // A click or tap on another segment ends a run of typing, as Tab does.
    this.typingStep = false;
    const next = focusSegment(state, index);
    this.state.set(next);
    this.emit(false, !sameEntry(state, next));
  }

  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next && this.host.nativeElement.contains(next)) {
      return;
    }
    document.removeEventListener('selectionchange', this.onSelectionChange);
    // A selection left in a segment takes the text an input method commits while focus is on
    // something that is no text box (a day or a time column in the panel): the browser writes it
    // there and focuses the segment. Only a window losing focus keeps it, to come back to.
    const selection = document.getSelection();
    if (selection?.anchorNode && this.host.nativeElement.contains(selection.anchorNode) && (next || document.hasFocus())) {
      selection.removeAllRanges();
    }
    this.setEditing(false);
    this.focused.set(false);
    this.state.set(blur(this.state()));
    this.emit(false, false);
    this.left.emit(event);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled() || event.isComposing || event.keyCode === 229) {
      return;
    }
    if (event.altKey && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      this.openRequested.emit('keyboard');
      return;
    }
    if (isUndo(event) || isRedo(event)) {
      event.preventDefault();
      if (this.editable()) {
        this.travel(isUndo(event) ? -1 : 1);
      }
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      // Ctrl+A has nothing to select; Ctrl+C, X and V arrive as copy, cut and paste.
      if (event.key.toLowerCase() === 'a') {
        event.preventDefault();
      }
      return;
    }
    if (event.altKey) {
      return;
    }
    const name = event.key === 'Tab' && event.shiftKey ? 'ShiftTab' : event.key;
    const printable = name.length === 1;
    if (!printable && !MODEL_KEYS.has(name) && name !== 'ShiftTab') {
      return;
    }
    if (!this.editable()) {
      // Readonly: Tab still moves between segments; nothing edits.
      if (name !== 'Tab' && name !== 'ShiftTab') {
        event.preventDefault();
      }
      return;
    }
    if ((name === ' ' && coarsePrimary() && this.togglePeriod()) || keepsKeptText(this.state(), name)) {
      event.preventDefault();
      return;
    }
    const result = key(this.state(), name, this.context());
    if (result.exit) {
      // Tab past the last segment, or Shift+Tab before the first: the browser moves focus on.
      if (result.state !== this.state()) {
        this.update(result);
      }
      return;
    }
    if (this.state().kept !== null && result.state === this.state()) {
      // A key kept text does not give way to: the browser's own (Tab, arrows, Home, End), or a separator, nobody's.
      if (printable) {
        event.preventDefault();
      }
      return;
    }
    event.preventDefault();
    this.update(result, printable);
  }

  /** The browser never edits a segment: every edit is cancelled and made in the model, once. */
  onBeforeInput(event: InputEvent): void {
    if (event.inputType === 'insertCompositionText') {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    this.composed = false;
    if (!this.editable()) {
      return;
    }
    const type = event.inputType;
    if (type === 'historyUndo' || type === 'historyRedo') {
      this.travel(type === 'historyUndo' ? -1 : 1);
      return;
    }
    if (type === 'insertFromPaste' || type === 'insertFromDrop') {
      this.insertText(event.data ?? event.dataTransfer?.getData('text/plain') ?? '', true);
      return;
    }
    if (type === 'insertText') {
      this.insertText(event.data ?? '', false);
      return;
    }
    const deletion = DELETIONS[type];
    if (deletion && !keepsKeptText(this.state(), deletion)) {
      this.update(key(this.state(), deletion, this.context()));
    }
  }

  /** An `input` the field did not cancel (an Android keyboard composing, with or without `compositionstart`). */
  onInput(event: Event): void {
    const data = (event as InputEvent).data;
    const composing = (event as InputEvent).isComposing;
    this.render();
    if (composing || this.composed || !data || !this.editable()) {
      this.composed = false;
      return;
    }
    this.insertText(data, false);
  }

  onCompositionStart(): void {
    this.composed = false;
  }

  /** The composed text, applied once to the segment that was focused; the browser's text goes. */
  onCompositionEnd(event: CompositionEvent): void {
    this.render();
    if (!this.editable() || !event.data) {
      return;
    }
    this.composed = true;
    this.insertText(event.data, false);
  }

  /**
   * A paste, a drop or a copy goes to the page's selection, which a press beside the field at rest can
   * leave in a segment with focus on the page: only with focus here is it the field's.
   */
  onPaste(event: ClipboardEvent): void {
    if (!this.focused()) {
      return;
    }
    event.preventDefault();
    if (this.editable()) {
      this.insertText(event.clipboardData?.getData('text/plain') ?? '', true);
    }
  }

  onDrop(event: DragEvent): void {
    if (!this.focused()) {
      return;
    }
    event.preventDefault();
    if (this.editable()) {
      this.insertText(event.dataTransfer?.getData('text/plain') ?? '', true);
    }
  }

  /** Copy and cut put the text the field shows on the clipboard; neither empties the field. */
  onCopy(event: ClipboardEvent): void {
    if (!this.focused()) {
      return;
    }
    event.preventDefault();
    event.clipboardData?.setData('text/plain', this.shownText().trim());
  }

  /**
   * A press on a segment focuses it (the browser's). A press on the group's blank end focuses the
   * first empty segment (or the last) and, with a fine pointer, opens the panel.
   */
  onMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.indexOf(target) >= 0 || target === this.keptEl().nativeElement || this.disabled()) {
      return;
    }
    event.preventDefault();
    this.focus(this.state().segs.some((seg) => seg.value === null) ? 'firstEmpty' : 'last');
    if (this.editable() && !coarsePrimary()) {
      this.openRequested.emit('pointer');
    }
  }

  /**
   * Before the browser decides focus: a press on a segment or kept text finds it editable, so a phone
   * raises its keyboard for the tap. Also whether it lands on the AM/PM segment that already has focus.
   */
  onPointerDown(event: PointerEvent): void {
    const index = this.indexOf(event.target);
    if (index >= 0 || event.target === this.keptEl().nativeElement) {
      this.setEditing(true);
    }
    this.pressedFocusedPeriod = index >= 0 && this.focused() && index === this.state().focus && this.kinds()[index] === 'period';
  }

  /** A finger that went on to scroll the page never pressed the segment it started on. */
  onPointerCancel(): void {
    this.setEditing(this.hasFocus());
  }

  /**
   * A tap on AM/PM under a coarse pointer toggles it, as Space does, so no letter keyboard has to
   * come up; the tap that only brings focus to it does not.
   */
  onClick(): void {
    if (this.pressedFocusedPeriod && coarsePrimary()) {
      this.togglePeriod();
    }
    this.pressedFocusedPeriod = false;
  }

  // ─── Internals ─────────────────────────────────────────────

  /**
   * Space, or a tap under a coarse pointer, on the AM/PM segment: toggles it; an empty one takes the
   * half the one rule gives the hour (AM with no hour).
   */
  private togglePeriod(): boolean {
    const state = this.state();
    const seg = state.segs[state.focus];
    if (state.kept !== null || seg?.kind !== 'period' || !this.editable()) {
      return false;
    }
    const hour = state.segs.find((s) => s.kind === 'hour')?.value ?? null;
    const value = seg.value !== null ? 1 - seg.value : hour !== null && (hour <= 6 || hour === 12) ? 1 : 0;
    this.update({ state: { ...state, fresh: false, segs: state.segs.map((s, i) => (i === state.focus ? { ...s, value, typed: '', refused: null } : s)) } });
    return true;
  }

  private insertText(data: string, paste: boolean): void {
    if (!data) {
      return;
    }
    const text = data.normalize('NFKC');
    const context = this.context();
    const result = text.length === 1 && !paste ? key(this.state(), text, context) : insert(this.state(), text, context, paste);
    this.update(result, !paste);
  }

  /** A key, paste or deletion; `typing` for characters typed (a key, or an input method's text), not pasted. */
  private update(result: SbKeyResult, typing = false): void {
    this.beginVisit();
    const before = this.state();
    this.set(result.state, typing);
    const said = result.refusal ?? result.announce;
    if (said) {
      this.announcer.announce(said, 'polite');
    }
    this.emit(!!result.filled, !sameEntry(before, result.state));
  }

  /**
   * A new state: rendered, focus following it, and a history step when it shows something else.
   * A state with a digit waiting is no step (Ctrl+Z drops the digit); a state that shows the current
   * step again (the same value typed back) replaces it rather than adding one; and characters typed
   * in one go, with no move, deletion, step, paste or pick between them, are one step, as a text
   * box's typing is: Ctrl+Z after a whole value typed over another brings the other back whole.
   */
  private set(next: FieldState, typing = false, step?: object): void {
    if (this.inVisit) {
      const current = this.history[this.at];
      this.history = this.history.slice(0, this.at + 1);
      const joins = !!current && (sameEntry(current, next) || (typing && this.typingStep));
      if (!waiting(next) && !joins) {
        if (step) {
          this.together.set(next, step);
        }
        this.history = [...this.history, next].slice(-HISTORY);
        this.at = this.history.length - 1;
        this.typingStep = typing;
      } else {
        if (!waiting(next)) {
          // The step shown again stays the one it was: one made with other fields still is (two
          // range presets that end on the same day leave the end's step as the first made it).
          const made = current && this.together.get(current);
          if (made) {
            this.together.set(next, made);
          }
          this.history[this.at] = next;
        }
        this.typingStep = typing && this.typingStep;
      }
    }
    this.state.set(next);
    this.render();
  }

  private emit(filled: boolean, changed = true, crossed?: SbFieldEdit['crossed']): void {
    this.edited.emit({ state: this.state(), filled, changed, crossed });
  }

  /**
   * Ctrl+Z and Ctrl+Y: whole states the field showed. The first undo after kept text gave way brings
   * it back; an undo while a digit waits goes back to the step it was typed over. Going over a step
   * made together with other fields says so, so they go over theirs (`follow`); `lead` is false
   * when this field is the one following.
   */
  private travel(direction: -1 | 1, lead = true): void {
    if (direction < 0 && this.keptGaveWay()) {
      this.restoreKept();
      return;
    }
    const current = this.history[this.at];
    const to = direction < 0 && current && !sameEntry(current, this.state()) ? this.at : this.at + direction;
    if (to < 0 || to >= this.history.length) {
      return;
    }
    const step = to === this.at ? undefined : this.together.get(this.history[direction < 0 ? this.at : to]);
    this.at = to;
    this.typingStep = false;
    const entry = this.history[to];
    this.state.set({ ...entry, focus: this.state().focus, run: this.state().run, fresh: true, rejected: null });
    this.render();
    this.emit(false, true, step && lead ? { step, direction } : undefined);
  }

  /** Focus from here finds the element editable too: it gets a caret, and on a phone the keyboard. */
  private focusElement(el: HTMLElement | undefined, preventScroll: boolean): void {
    this.setEditing(true);
    el?.focus({ preventScroll });
    this.setEditing(this.hasFocus());
  }

  private setEditing(on: boolean): void {
    if (on !== this.editing) {
      this.editing = on;
      document[on ? 'addEventListener' : 'removeEventListener']('mousedown', this.onPagePress, true);
      this.writeEditable();
    }
  }

  /** Written here, not bound: a press decides focus before change detection would have run. */
  private writeEditable(): void {
    const on = this.editable() && this.editing;
    [...this.segEls(), this.keptEl()].forEach(({ nativeElement: el }) => {
      if (el.hasAttribute('contenteditable') !== on) {
        return on ? el.setAttribute('contenteditable', 'true') : el.removeAttribute('contenteditable');
      }
    });
  }

  private beginVisit(): void {
    if (this.inVisit) {
      return;
    }
    this.inVisit = true;
    this.history = [this.state()];
    this.at = 0;
    this.typingStep = false;
    this.keptEntry = this.state().kept !== null ? this.state() : null;
  }

  private context(): SbSegContext {
    const now = nowIn(this.zone());
    return { today: now.date, now: now.time };
  }

  private indexOf(target: EventTarget | null): number {
    return this.segEls().findIndex((el) => el.nativeElement === target);
  }

  /** There is no caret: a selection inside a segment collapses (React Aria), so typing never replaces "selected" text. */
  private collapseSelection(): void {
    const selection = document.getSelection();
    const node = selection?.anchorNode;
    if (selection && node && this.host.nativeElement.contains(node) && !selection.isCollapsed) {
      selection.collapse(node, 0);
    }
  }
}

/** Sets an element's text only when it differs, so a composition's node is replaced only when needed. */
const setText = (el: HTMLElement, text: string): void => {
  if (el.textContent !== text) {
    el.textContent = text;
  }
};
